import { z } from 'zod';
import { isValidPhoneNum } from '../validations';

export const stringFieldSchema = (fieldName: string, message: string) =>
  z.object({
    [fieldName]: z.string().refine(
      (val) => {
        return val.length > 0;
      },
      {
        message,
      },
    ),
  });

export const arrayFieldSchema = (fieldName: string, message: string) =>
  z.object({
    [fieldName]: z.array(z.string()).refine((val) => val.length > 0, {
      message,
    }),
  });

export const stringConstraints = (message: string) =>
  z.string().min(1, {
    message,
  });

export const phoneNumberValidation = (
  message: string,
  messageInvalid: string,
) =>
  z.string().superRefine((val, ctx) => {
    if (val.length === 0) {
      ctx.addIssue({
        message,
        code: z.ZodIssueCode.custom,
      });
    } else if (!isValidPhoneNum(val)) {
      ctx.addIssue({
        message: messageInvalid,
        code: z.ZodIssueCode.custom,
      });
    }
  });

export const EmailConfirmationReportOptionsSchema = stringFieldSchema(
  'canWeEmailYou',
  'Select if you want to receive a confirmation email',
);

export const contactUserOptionsSchema = stringFieldSchema(
  'canWeContactYou',
  'Please select an option to proceed',
);

export const issueTypeSingleSelectionSchema = stringFieldSchema(
  'issueType',
  'Please select an option to proceed',
);

export const issueTypeSchema = arrayFieldSchema(
  'issueTypes',
  'Please select at least one option',
);

export const isIssueHappeningNowSchema = stringFieldSchema(
  'isIssueHappeningNow',
  'Select an option to continue',
);

const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth();
const currentDate = new Date();
const currentDateSetter = new Date(currentDate);
currentDateSetter.setDate(currentDate.getDate());
const twelveMonthsAgo = new Date();
twelveMonthsAgo.setMonth(currentDate.getMonth() - 12);

const oneDayLater = new Date(twelveMonthsAgo);
oneDayLater.setDate(oneDayLater.getDate() + 1);

export const IssueDateAndTimeSchema = z.object({
  selectedDay: stringConstraints('day is required'),
  selectedMonth: stringConstraints('month is required'),
  selectedYear: stringConstraints('year is required'),
  selectedMinute: stringConstraints('minute is required'),
  selectedHour: stringConstraints('hour is required'),
});

export const IssueDateAndTimeRangeSchema = z
  .object({
    dayRange: z.preprocess(
      (val) => String(val),
      z.string().refine((val) => /^\d+$/.test(val), {
        message: 'Day must be a number',
      }),
    ),
    monthRange: z.preprocess(
      (val) => String(val),
      z
        .string()
        .refine((val) => /^\d+$/.test(val), {
          message: 'Month must be a number',
        })

        .transform((val) => Number(val))
        .refine(
          (val) => {
            return val >= 1 && val <= 12;
          },
          {
            message: 'Month must be between 1 and 12',
          },
        ),
    ),
    twelveMonthsRange: z.preprocess(
      (val) =>
        typeof val === 'string' || val instanceof Date ? new Date(val) : val,
      z
        .date()
        .refine((date) => date <= currentDate && date >= twelveMonthsAgo, {
          message: `Date must be between ${oneDayLater.toISOString().slice(0, 10).split('-').reverse().join('-')} and ${currentDateSetter.toISOString().slice(0, 10).split('-').reverse().join('-')}`,
        }),
    ),
    yearRange: z.preprocess(
      (val) => String(val),
      z
        .string()
        .refine((val) => /^\d+$/.test(val), {
          message: 'Year must be a number',
        })
        .transform((val) => Number(val))
        .refine((val) => val >= currentYear - 1 && val <= currentYear, {
          message: `Year must be between ${currentYear - 1} and ${currentYear}`,
        }),
    ),
    minuteRange: z.preprocess(
      (val) => String(val),
      z
        .string()
        .refine((val) => /^\d+$/.test(val), {
          message: 'Minute must be a number',
        })
        .transform((val) => Number(val))
        .refine((val) => val >= 0 && val <= 59, {
          message: 'Minute must be between 0 and 59',
        }),
    ),
    hourRange: z.preprocess(
      (val) => String(val),
      z
        .string()
        .refine((val) => /^\d+$/.test(val), {
          message: 'Hour must be a number',
        })
        .transform((val) => Number(val))
        .refine((val) => val >= 0 && val <= 23, {
          message: 'Hour must be between 0 and 23',
        }),
    ),
  })
  .superRefine(
    ({ dayRange, monthRange, yearRange, hourRange, minuteRange }, ctx) => {
      const firstDate = new Date(
        yearRange,
        Number(monthRange) - 1,
        1,
      ).getDate();
      const lastDate = new Date(yearRange, Number(monthRange), 0).getDate();
      const currentMonth = new Date().getMonth() + 1;
      const currentDay = new Date().getDate();
      const currentHour = new Date().getHours();
      const currentMinute = new Date().getMinutes();
      if (Number(dayRange) < firstDate || Number(dayRange) > lastDate) {
        ctx.addIssue({
          message: `Day must be between ${firstDate} and ${lastDate}`,
          code: z.ZodIssueCode.custom,
          path: ['dayRange'],
        });
      }
      // below validations are to prevent user from entering future date and time
      if (
        yearRange === currentYear &&
        monthRange > currentMonth &&
        monthRange <= 12
      ) {
        ctx.addIssue({
          message: `Month must be between 1 and ${currentMonth}`,
          code: z.ZodIssueCode.custom,
          path: ['monthRange'],
        });
      }
      if (
        yearRange === currentYear &&
        monthRange === currentMonth &&
        Number(dayRange) > currentDay &&
        Number(dayRange) <= lastDate
      ) {
        ctx.addIssue({
          message: `Day must be between ${firstDate} and ${currentDay}`,
          code: z.ZodIssueCode.custom,
          path: ['dayRange'],
        });
      }
      if (
        yearRange === currentYear &&
        monthRange === currentMonth &&
        Number(dayRange) === currentDay &&
        hourRange > currentHour &&
        hourRange < 24
      ) {
        ctx.addIssue({
          message: `Hour must be between 0 and ${currentHour}`,
          code: z.ZodIssueCode.custom,
          path: ['hourRange'],
        });
      }
      if (
        yearRange === currentYear &&
        monthRange === currentMonth &&
        Number(dayRange) === currentDay &&
        hourRange === currentHour &&
        minuteRange > currentMinute &&
        minuteRange < 60
      ) {
        ctx.addIssue({
          message: `Minute must be between 0 and ${currentMinute}`,
          code: z.ZodIssueCode.custom,
          path: ['minuteRange'],
        });
      }
    },
  );
